import Url from "./snake.lib.url.js";
import Storage from "./snake.lib.storage.js";
import config from "../config.js";

export default class Identity {
    _country;
    _store;

    static _gproxyLoader = async (token = false) => {
        this._country = Url.getCurrentUri().split("/")[1];
        this._store = import.meta.env.CO_BRAND === "deagostini" ? "PW" : "FH";

        return new Promise((resolve, reject) => {
            const script = document.createElement("script");

            script.onload = () => Identity.fetcher.initializeProxy(token, resolve, reject);

            let proxyKey = import.meta.env.CO_PROXY_KEY;

            switch (this._country) {
                case "at":
                    proxyKey = import.meta.env.CO_PROXY_KEY_AT;
                    break;
                case "us":
                    proxyKey = import.meta.env.CO_PROXY_KEY_US;
                    break;
                case "be":
                    proxyKey = import.meta.env.CO_PROXY_KEY_BE;
                    break;
            }

            script.src = import.meta.env.CO_GPROXY_URL.replace("_KEY_", proxyKey) + "&site=" + import.meta.env.CO_BRAND + "&lang=" + this._getLanguageByCountry(this._country) + "&store=" + this._store + this._country.toUpperCase();

            document.head.appendChild(script);
        });
    };

    static _gaddressLoader = async () => {
        this._country = Url.getCurrentUri().split("/")[1];
        this._store = import.meta.env.CO_BRAND === "deagostini" ? "PW" : "FH";

        return new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.onload = () => Identity.fetcher.initializeAddress(resolve, reject);
            script.src = import.meta.env.CO_GADDRESS_URL;
            document.head.appendChild(script);

            window.snakeCheckout.gaddressLoaded = true;
        });
    };

    static _getLanguageByCountry = (country) => {
        switch (country) {
            case "us":
            case "uk":
                return "en";
            case "at":
                return "de";
            case "gr":
                return "el";
            case "cz":
                return "cs";
            case "be":
                return "fr";
        }

        return country;
    };

    static _getLocaleByCountry = (country) => {
        switch (country) {
            case "uk":
                return "en_GB";
            case "us":
                return "en_US";
            case "at":
                return "de_AT";
            case "gr":
                return "el_GR";
            case "cz":
                return "cs_CZ";
            case "be":
                return "fr_BE";
        }

        return country + "_" + country.toUpperCase();
    };

    static fetcher = {
        initializeProxy: (token = false, resolve, reject) => {
            window.gproxy(token, () => {
                // without extensions
                resolve({ result: true, target: window.gproxy });

                // with extensions
                // window.gproxy.extension
                //   .install("gcustomer") // TODO: remove useless extension
                //   .then(() => {
                //     resolve({ result: true, target: window.gproxy });

                //     Hook.publish("_gproxyInitialized");
                //   })
                //   .catch((error) => {
                //     // TODO: show popup

                //     // Trace.debug("Error loading gcustomer extension");

                //     reject({ error });
                //   });
            });
        },
        initializeAddress: (resolve, reject) => {
            window.gaddress(this._getLanguageByCountry(this._country), this._country, function () {
                resolve({ result: true, target: window.gaddress });
            });
        },
        // redo: (resolve, reject) => resolve({ result: true, target: window.gproxy }),
        // test: () => {
        //     return (
        //         typeof window.gproxy !== "undefined" &&
        //         typeof window.gproxy.session !== "undefined"
        //     );
        // },
    };

    static isAgent() {
        return Storage.get("agent") === "true";
    }

    static isAgentCustomer() {
        return Storage.get("agentCustomer") === "true";
    }

    static isAuth() {
        return window.gproxy.session.valid();
    }

    static signUp = async (firstName, lastName, email) => {
        return new Promise((resolve, reject) => {
            window.gproxy.accounts.initRegistration({
                callback: (response) => {
                    const country = Identity._country;

                    window.gproxy.accounts.register({
                        email,
                        password: window.gproxy.security.random(),
                        regToken: response.regToken,
                        finalizeRegistration: true,
                        // sessionExpiration: -2,
                        preferences: window.gproxy.consents.serialize({
                            profile: country === "us" ? "us" : "default",
                            input: {
                                GDPRPromo: false,
                                GDPRPromo3rd: false,
                                GDPRProfiling: false,
                                GDPROptInSMS: false,
                            },
                        }),
                        subscriptions: {
                            marketing: {
                                email: {
                                    isSubscribed: false,
                                },
                            },
                            newsletter: {
                                email: {
                                    isSubscribed: false,
                                },
                            },
                        },
                        profile: {
                            firstName,
                            lastName,
                            // email,
                            locale: Identity._getLocaleByCountry(country),
                        },
                        data: {
                            commerceId: "",
                            acquisitionSource: "E-COMMERCE",
                            acquisitionOwner: Identity._store + country.toUpperCase(),
                            identityType: 2,
                            lastSysModBy: "COM",
                        },
                        callback: (response) => (response.errorCode === 0 ? resolve(response) : reject(response)),
                    });
                },
            });
        });
    };

    static signIn = async (email, password, rememberMe) => {
        return new Promise((resolve, reject) => {
            window.gproxy.accounts.login({
                loginID: email,
                password,
                sessionExpiration: rememberMe ? -2 : 0,
                callback: (response) => (response.errorCode === 0 ? resolve(response) : reject(response)),
            });
        });
    };

    static signOut = async () => {
        return new Promise((resolve, reject) => {
            window.gproxy.accounts.logout({
                callback: (response) => (response.errorCode === 0 ? resolve(response) : reject(response)),
            });
        });
    };

    static getAccountInfo = async () => {
        return new Promise((resolve, reject) => {
            window.gproxy.accounts.getAccountInfo({
                extraProfileFields: "phones",
                include: "profile,data,preferences,",
                callback: (response) => (response.errorCode === 0 ? resolve(response) : reject(response)),
            });
        });
    };

    static setAccountInfo = async (firstname, lastname, email, commerceId) => {
        return new Promise((resolve, reject) => {
            window.gproxy.accounts.setAccountInfo({
                profile: {
                    firstName: firstname,
                    lastName: lastname,
                    email,
                },
                data: {
                    commerceId,
                },
                callback: (response) => (response.errorCode === 0 ? resolve(response) : reject(response)),
            });
        });
    };

    static setAddresses = async (shippingAddresses, billingAddress) => {
        return new Promise((resolve, reject) => {
            /**
             * BEGIN FIX
             *
             * FIX needed for the removal of the "normStreetNum" field from the shipping and billing forms
             * for "cz", "gr", "it", "pl", "sk" and "uk" countries
             */
            shippingAddresses.forEach((address, index) => {
                if (["CZE", "GRC", "ITA", "POL", "SVK", "GBR"].includes(address.normCountryCode || "")) shippingAddresses[index].normStreetNum = "";
            });

            if (["CZE", "GRC", "ITA", "POL", "SVK", "GBR"].includes(billingAddress.normCountryCode || "")) billingAddress.normStreetNum = "";
            /**
             * END FIX
             */

            delete billingAddress.label;
            delete billingAddress.defaultAddress;

            window.gproxy.accounts.setAccountInfo({
                data: {
                    shippingAddress: shippingAddresses,
                    billingAddress,
                },
                callback: (response) => (response.errorCode === 0 ? resolve(response) : reject(response)),
            });
        });
    };

    static getConsents = async () => {
        return new Promise((resolve, reject) => {
            const currentCountry = Identity._country.toLowerCase();

            window.gproxy.consents.profile({
                profile: ["us", "at"].includes(currentCountry) ? currentCountry : "default",
                lang: config.countries[currentCountry].lang || currentCountry,
                store: Identity._store + currentCountry.toUpperCase(),
                require: window.gproxy.consents.grants.MISSING_ONLY,
                callback: (response) => resolve(response),
            });
        });
    };

    static setConsents = async (checkedConsents) => {
        return new Promise((resolve, reject) => {
            const currentCountry = Identity._country.toLowerCase();

            window.gproxy.accounts.setAccountInfo({
                preferences: window.gproxy.consents.serialize({
                    profile: ["us", "at"].includes(currentCountry) ? currentCountry : "default",
                    input: checkedConsents,
                }),
                subscriptions: {
                    marketing: {
                        email: {
                            isSubscribed: checkedConsents.GDPRPromo || false,
                        },
                    },
                    newsletter: {
                        email: {
                            isSubscribed: checkedConsents.GDPRPromo || false,
                        },
                    },
                },
                callback: (response) => (response.errorCode === 0 ? resolve(response) : reject(response)),
            });
        });
    };

    static purchaseLookup = async (product) => {
        return new Promise((resolve, reject) => {
            window.gproxy.purchase
                .lookup(product.code)
                .then((response) => resolve(response))
                .catch((response) => reject(response));
        });
    };

    static purchaseAdd = async (productCode) => {
        return new Promise((resolve, reject) => {
            window.gproxy.purchase
                .add({ product: productCode })
                .then((response) => resolve(response))
                .catch((response) => reject(response));
        });
    };

    static agentSignIn = async (emailOrGigyaID, secret) => {
        return new Promise((resolve, reject) => {
            window.gproxy.protected.authenticate({
                client: "agent-console",
                secret,
                handle: emailOrGigyaID,
                callback: (response) => (response.errorCode === 0 ? resolve(response) : reject(response)),
            });
        });
    };

    // static getAvailablePromotions = async () => {
    //     return new Promise((resolve) => {
    //         window.gproxy.promos.list({
    //             callback: (response) => (response.length > 0 ? resolve(response) : resolve([])),
    //         });
    //     });
    // };

    static getAvailablePromotions = async (skus) => {
        return new Promise((resolve) => {
            window.gproxy.promos.list({
                cart: skus,
                callback: (response) => (response.length > 0 ? resolve(response) : resolve([])),
            });
        });
    };

    // static getSubscriptions = async (email) => {
    //   return new Promise((resolve, reject) => {
    //     window.gproxy.subscriptions.list({
    //       email,
    //       callback: function (response) {
    //         // Trace.debug("Identity getSubscriptions");
    //         // Trace.log(response);

    //         resolve(response);
    //       },
    //     });
    //   });
    // };
}
